import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'

type LocalStorageHookReturnType<T> = [T | null, (value: T) => void, () => void]

const getSnapshot = (storageKey: string) => {
  return localStorage.getItem(storageKey)
}

const subscribeToLocalStorage = (listener: () => void) => {
  window.addEventListener('storage', listener)

  return () => window.removeEventListener('storage', listener)
}

export const useLocalStorage = <T>(
  storageKey: string,
  defaultValue?: T | undefined,
  clearStorageOnInitial?: boolean,
): LocalStorageHookReturnType<T> => {
  const value = useSyncExternalStore(subscribeToLocalStorage, () => getSnapshot(storageKey))

  const updateStorage = useCallback(
    (value: T) => {
      localStorage.setItem(storageKey, JSON.stringify(value))
      window.dispatchEvent(new StorageEvent('storage'))
    },
    [storageKey],
  )

  if (getSnapshot(storageKey) == null && defaultValue != null) {
    updateStorage(defaultValue)
  }

  const clearStorage = useCallback(() => {
    localStorage.removeItem(storageKey)
    window.dispatchEvent(new StorageEvent('storage'))
  }, [storageKey])

  useEffect(() => {
    if (clearStorageOnInitial) {
      clearStorage()
    }
  }, [clearStorage, clearStorageOnInitial])

  const returnValue = useMemo(() => {
    const parsedValue = value == null ? null : (JSON.parse(value) as T | null)
    return [parsedValue, updateStorage, clearStorage] satisfies LocalStorageHookReturnType<T>
  }, [value, updateStorage, clearStorage])

  return returnValue
}
